import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

/**
 * Regime Context - Gerencia a visão de Caixa vs Competência
 *
 * CAIXA: Considera quando pagou/recebeu (payment_date)
 * COMPETÊNCIA: Considera quando foi gerado (competence_date ou issue_date)
 */

const RegimeContext = createContext({
  regime: 'cash', // 'cash' ou 'accrual'
  setRegime: () => {},
  regimeLabel: 'Caixa',
});

export const useRegime = () => {
  const context = useContext(RegimeContext);
  if (!context) {
    throw new Error('useRegime must be used within a RegimeProvider');
  }
  return context;
};

export function RegimeProvider({ children }) {
  const [regime, setRegimeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('axion-regime');
      if (stored && ['cash', 'accrual'].includes(stored)) {
        return stored;
      }
    }
    return 'cash';
  });

  const regimeLabel = regime === 'cash' ? 'Caixa' : 'Competência';

  // Persist regime change
  const setRegime = useCallback(async (newRegime) => {
    setRegimeState(newRegime);
    localStorage.setItem('axion-regime', newRegime);

    // Persist to backend if authenticated
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.put('/users/preferences', { regime_preference: newRegime });
      } catch (error) {
        console.warn('Failed to persist regime preference:', error);
      }
    }
  }, []);

  // Load regime from backend on auth
  useEffect(() => {
    const loadRegimeFromBackend = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await api.get('/users/preferences');
        const backendRegime = response.data.regime_preference;
        if (backendRegime && ['cash', 'accrual'].includes(backendRegime)) {
          setRegimeState(backendRegime);
          localStorage.setItem('axion-regime', backendRegime);
        }
      } catch (error) {
        // Ignore - use local storage value
      }
    };

    loadRegimeFromBackend();
  }, []);

  return (
    <RegimeContext.Provider value={{ regime, setRegime, regimeLabel }}>
      {children}
    </RegimeContext.Provider>
  );
}
